import type { ColorVariant } from "../../../shared/colors";

// Canonical taxonomy for shared button primitives:
// - `solid`: primary call-to-action
// - `outline`: secondary actions
// - `ghost`: low-emphasis inline actions
// - `link`: menu/text actions
// - `icon`: icon-only controls (use IconButton)
export const CANONICAL_BUTTON_VARIANTS = [
  "solid",
  "outline",
  "ghost",
  "link",
  "icon",
] as const;
export const CANONICAL_BUTTON_SIZES = ["xs", "sm", "md", "lg"] as const;
export const CANONICAL_BUTTON_TONES = [
  "primary",
  "secondary",
  "success",
  "danger",
  "warning",
  "info",
  "neutral",
] as const;

export type CanonicalButtonVariant = (typeof CANONICAL_BUTTON_VARIANTS)[number];
export type CanonicalNonIconVariant = Exclude<CanonicalButtonVariant, "icon">;
export type CanonicalButtonSize = (typeof CANONICAL_BUTTON_SIZES)[number];
export type CanonicalButtonTone = (typeof CANONICAL_BUTTON_TONES)[number];

// Migration guidance:
// 1) choose variant for emphasis, 2) choose tone for semantic intent,
// 3) only add `className` for layout or one-off product styling.

export const CANONICAL_BUTTON_BASE_CLASSES =
  "rounded-md transition-colors duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-boxdark disabled:cursor-not-allowed disabled:opacity-50";

export const CANONICAL_ICON_BUTTON_BASE_CLASSES =
  "rounded-full bg-white/80 dark:bg-neutral-800/80 transition-colors duration-200 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-boxdark disabled:cursor-not-allowed disabled:opacity-50";

export const BUTTON_SIZE_CLASSES: Record<CanonicalButtonSize, string> = {
  xs: "px-2.5 py-1.5 text-xs",
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2",
  lg: "px-5 py-2.5 text-base",
};

export const ICON_BUTTON_SIZE_CLASSES: Record<CanonicalButtonSize, string> = {
  xs: "p-1",
  sm: "p-1.5",
  md: "p-2",
  lg: "p-2.5",
};

export interface ButtonPrimitiveContract {
  variant: CanonicalButtonVariant;
  size: CanonicalButtonSize;
  tone: CanonicalButtonTone;
  loading: boolean;
  fullWidth: boolean;
  iconOnly: boolean;
}

export const BUTTON_PRIMITIVE_DEFAULTS: ButtonPrimitiveContract = {
  variant: "solid",
  size: "md",
  tone: "primary",
  loading: false,
  fullWidth: false,
  iconOnly: false,
};

export const LEGACY_COLOR_VARIANTS = [
  "primary",
  "secondary",
  "success",
  "danger",
  "warning",
  "info",
  "neutral",
] as const satisfies readonly ColorVariant[];

export type LegacyColorVariant = (typeof LEGACY_COLOR_VARIANTS)[number];

export const LEGACY_COLOR_TO_CANONICAL_TONE: Record<
  LegacyColorVariant,
  CanonicalButtonTone
> = {
  primary: "primary",
  secondary: "secondary",
  success: "success",
  danger: "danger",
  warning: "warning",
  info: "info",
  neutral: "neutral",
};

export function isLegacyColorVariant(
  value: string,
): value is LegacyColorVariant {
  return LEGACY_COLOR_VARIANTS.includes(value as LegacyColorVariant);
}

type ButtonPrimitiveComponent = "Button" | "IconButton";

export interface CanonicalMigrationTarget {
  component: ButtonPrimitiveComponent;
  variant: CanonicalButtonVariant;
  tone: CanonicalButtonTone;
  size: CanonicalButtonSize;
  fullWidth?: boolean;
  iconOnly?: boolean;
}

export interface LegacyButtonSignatureMapping {
  signature: string;
  occurrences: number;
  scopeFiles: readonly string[];
  target: CanonicalMigrationTarget;
}

export const BUTTON_M1_BASELINE = {
  scope: "plans/button-primitive-standardization.md (in-scope file list)",
  measuredAt: "2026-02-24",
  rawButtonUsageCount: 28,
  repeatedSignatures: [
    {
      signature:
        "flex w-full justify-center rounded-sm bg-primary p-3 font-medium text-gray",
      occurrences: 3,
    },
    {
      signature:
        "flex justify-center rounded-sm border border-stroke py-2 px-6 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white",
      occurrences: 2,
    },
    {
      signature: "text-sm hover:text-primary",
      occurrences: 2,
    },
  ],
} as const;

export const BUTTON_M1_MIGRATION_TARGETS: readonly LegacyButtonSignatureMapping[] =
  [
    {
      signature:
        "flex w-full justify-center rounded-sm bg-primary p-3 font-medium text-gray",
      occurrences: 3,
      scopeFiles: [
        "archive/admin-dashboard/src/admin/elements/forms/FormLayoutsPage.tsx",
      ],
      target: {
        component: "Button",
        variant: "solid",
        tone: "primary",
        size: "md",
        fullWidth: true,
      },
    },
    {
      signature:
        "flex justify-center rounded-sm border border-stroke py-2 px-6 font-medium text-black hover:shadow-1 dark:border-strokedark dark:text-white",
      occurrences: 2,
      scopeFiles: [
        "optimistic-news/app/src/admin/elements/settings/SettingsPage.tsx",
      ],
      target: {
        component: "Button",
        variant: "outline",
        tone: "neutral",
        size: "sm",
      },
    },
    {
      signature: "text-sm hover:text-primary",
      occurrences: 2,
      scopeFiles: [
        "optimistic-news/app/src/admin/elements/settings/SettingsPage.tsx",
      ],
      target: {
        component: "Button",
        variant: "link",
        tone: "primary",
        size: "sm",
      },
    },
    {
      signature:
        "flex justify-center rounded-sm bg-primary py-2 px-6 font-medium text-gray hover:shadow-1",
      occurrences: 1,
      scopeFiles: [
        "optimistic-news/app/src/admin/elements/settings/SettingsPage.tsx",
      ],
      target: {
        component: "Button",
        variant: "solid",
        tone: "primary",
        size: "sm",
      },
    },
    {
      signature:
        "flex justify-center rounded-sm bg-primary py-2 px-6 font-medium text-gray hover:bg-primary/70",
      occurrences: 1,
      scopeFiles: [
        "optimistic-news/app/src/admin/elements/settings/SettingsPage.tsx",
      ],
      target: {
        component: "Button",
        variant: "solid",
        tone: "primary",
        size: "sm",
      },
    },
    {
      signature:
        "rounded-sm border border-stroke px-4 py-2 text-black hover:bg-neutral-100 transition-colors dark:border-strokedark dark:text-white dark:hover:bg-neutral-800",
      occurrences: 1,
      scopeFiles: [
        "optimistic-news/app/src/admin/dashboards/articles/ArticlesTable.tsx",
      ],
      target: {
        component: "Button",
        variant: "outline",
        tone: "neutral",
        size: "sm",
      },
    },
    {
      signature:
        "rounded-sm bg-danger-600 px-4 py-2 text-white hover:bg-danger-700 transition-colors",
      occurrences: 1,
      scopeFiles: [
        "optimistic-news/app/src/admin/dashboards/articles/ArticlesTable.tsx",
      ],
      target: {
        component: "Button",
        variant: "solid",
        tone: "danger",
        size: "sm",
      },
    },
    {
      signature:
        "p-2 rounded-full bg-white/80 dark:bg-neutral-800/80 hover:bg-primary hover:text-white dark:hover:bg-primary dark:hover:text-white transition-colors",
      occurrences: 1,
      scopeFiles: ["optimistic-news/app/src/components/common/IconButton.tsx"],
      target: {
        component: "IconButton",
        variant: "icon",
        tone: "primary",
        size: "md",
        iconOnly: true,
      },
    },
  ] as const;

export const BUTTON_M1_MIGRATION_TARGET_INDEX: Record<
  string,
  CanonicalMigrationTarget
> = BUTTON_M1_MIGRATION_TARGETS.reduce<
  Record<string, CanonicalMigrationTarget>
>((acc, entry) => {
  acc[entry.signature] = entry.target;
  return acc;
}, {});
